import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type ReactNode,
} from 'react';
import { Link, type LinkProps } from 'react-router';
import { errorMessage } from '../api/errors';
import { PairingMark } from '../brand/PairingMark';
import s from './ui.module.css';

// Form fields live in fields.tsx (no router import, so the public pages can use them); re-exported here.
export { Combobox, Field, Input, Select, Textarea, type DropdownChange } from './fields';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger';

const cx = (...c: Array<string | false | null | undefined>) => c.filter(Boolean).join(' ');

function variantClass(v: Variant): string {
  return { primary: s.primary, secondary: s.secondary, ghost: s.ghost, danger: s.danger }[v]!;
}

export function Button({
  variant = 'secondary',
  compact,
  busy,
  className,
  children,
  type = 'button',
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant; compact?: boolean; busy?: boolean }) {
  return (
    <button type={type} className={cx(s.button, variantClass(variant), compact && s.compact, className)} aria-busy={busy || undefined} {...rest}>
      {children}
    </button>
  );
}

export function ButtonLink({ variant = 'secondary', compact, className, ...rest }: LinkProps & { variant?: Variant; compact?: boolean }) {
  return <Link className={cx(s.button, variantClass(variant), compact && s.compact, className)} {...rest} />;
}

/** A primary button's look with no behaviour, for previews (branding). */
export function ButtonPreview({ children }: { children: ReactNode }) {
  return <span className={cx(s.button, s.primary, s.compact)}>{children}</span>;
}

export function Actions({ children }: { children: ReactNode }) {
  return <div className={s.actions}>{children}</div>;
}

/** An icon-only button. `label` is its accessible name and the tooltip shown on hover and focus. */
export function IconButton({
  label,
  busy,
  className,
  children,
  type = 'button',
  ...rest
}: Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'aria-label'> & { label: string; busy?: boolean }) {
  return (
    <button type={type} className={cx(s.iconButton, className)} aria-label={label} data-tip={label} aria-busy={busy || undefined} {...rest}>
      {children}
    </button>
  );
}

/** An on/off setting: a real checkbox with role="switch", its label and one line of explanation. `hideLabel` keeps the label for screen readers only. */
export function Switch({
  checked,
  onChange,
  label,
  description,
  hideLabel,
  disabled,
  title,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  label: ReactNode;
  description?: ReactNode;
  hideLabel?: boolean;
  disabled?: boolean;
  title?: string;
}) {
  return (
    <label className={s.switch} title={title}>
      <input type="checkbox" role="switch" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
      <span className={s.track} aria-hidden="true" />
      <span className={cx(s.switchText, hideLabel && s.srOnly)}>
        <span className={s.label}>{label}</span>
        {description && <span className={s.hint}>{description}</span>}
      </span>
    </label>
  );
}

export function Panel({ title, actions, children, id }: { title?: ReactNode; actions?: ReactNode; children: ReactNode; id?: string }) {
  const headingId = useId();
  return (
    <section className={s.panel} aria-labelledby={title ? headingId : undefined} id={id}>
      {(title || actions) && (
        <div className={s.panelHeader}>
          {title && <h2 id={headingId}>{title}</h2>}
          {actions}
        </div>
      )}
      {children}
    </section>
  );
}

/** `help` is a link to the page's help article ("How this works"), shown under the description. */
export function PageHeader({ title, description, actions, help }: { title: ReactNode; description?: ReactNode; actions?: ReactNode; help?: ReactNode }) {
  return (
    <header className={s.pageHeader}>
      <div>
        <h1>{title}</h1>
        {description && <p>{description}</p>}
        {help && <p>{help}</p>}
      </div>
      {actions && <Actions>{actions}</Actions>}
    </header>
  );
}

/** The small uppercase label that heads a block inside a card. */
export function SectionLabel({ children }: { children: ReactNode }) {
  return <div className={s.sectionLabel}>{children}</div>;
}

/** The rust uppercase line above a title. */
export function Eyebrow({ children }: { children: ReactNode }) {
  return <div className={s.eyebrow}>{children}</div>;
}

export function Stack({ children }: { children: ReactNode }) {
  return <div className={s.stack}>{children}</div>;
}

export function Banner({ tone = 'info', title, children, actions }: { tone?: 'info' | 'review' | 'error' | 'success'; title?: ReactNode; children?: ReactNode; actions?: ReactNode }) {
  const toneClass = { info: s.bannerInfo, review: s.bannerReview, error: s.bannerError, success: s.bannerSuccess }[tone];
  return (
    <div className={cx(s.banner, toneClass)} role={tone === 'error' ? 'alert' : 'status'}>
      <div className={s.bannerBody}>
        {title && <strong>{title}</strong>}
        {children && <div>{children}</div>}
      </div>
      {actions}
    </div>
  );
}

export function EmptyState({ title, children, action, level = 2 }: { title: ReactNode; children?: ReactNode; action?: ReactNode; level?: 2 | 3 }) {
  const H = level === 2 ? 'h2' : 'h3';
  return (
    <div className={s.empty}>
      <H>{title}</H>
      {children && <p>{children}</p>}
      {action && <div>{action}</div>}
    </div>
  );
}

export function Loading({ label = 'Loading' }: { label?: string }) {
  return (
    <p className={s.loading} role="status">
      {label}…
    </p>
  );
}

/** Before any frame exists (sign-in check, tenant lookup): the mark, centred, and nothing else. */
export function PageLoading({ label = 'Loading' }: { label?: string }) {
  return (
    <div className={s.pageLoading} role="status" aria-label={label}>
      <PairingMark variant="light" height={30} decorative />
      <span>{label}…</span>
    </div>
  );
}

export function ErrorState({ error, retry }: { error: unknown; retry?: () => void }) {
  return (
    <Banner tone="error" title="This could not be loaded." actions={retry ? <Button onClick={retry}>Try again</Button> : undefined}>
      {errorMessage(error)}
    </Banner>
  );
}

export function Muted({ children }: { children: ReactNode }) {
  return <span className={s.muted}>{children}</span>;
}

export function Mono({ children }: { children: ReactNode }) {
  return <span className={s.mono}>{children}</span>;
}

export function Definitions({ items }: { items: Array<[ReactNode, ReactNode]> }) {
  return (
    <dl className={s.definitions}>
      {items.map(([k, v], i) => (
        <div key={i} style={{ display: 'contents' }}>
          <dt>{k}</dt>
          <dd>{v}</dd>
        </div>
      ))}
    </dl>
  );
}

// ---------- tabs ----------

export function Tabs<T extends string>({ tabs, value, onChange, label }: { tabs: Array<{ id: T; label: ReactNode }>; value: T; onChange: (id: T) => void; label: string }) {
  const refs = useRef<Array<HTMLButtonElement | null>>([]);
  const move = (i: number) => {
    const next = (i + tabs.length) % tabs.length;
    onChange(tabs[next]!.id);
    refs.current[next]?.focus();
  };
  return (
    <div className={s.tabs} role="tablist" aria-label={label}>
      {tabs.map((t, i) => (
        <button
          key={t.id}
          ref={(el) => {
            refs.current[i] = el;
          }}
          type="button"
          role="tab"
          id={`tab-${t.id}`}
          aria-controls={`panel-${t.id}`}
          aria-selected={t.id === value}
          tabIndex={t.id === value ? 0 : -1}
          className={s.tab}
          onClick={() => onChange(t.id)}
          onKeyDown={(e) => {
            if (e.key === 'ArrowRight') move(i + 1);
            if (e.key === 'ArrowLeft') move(i - 1);
          }}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

export function TabPanel({ id, children }: { id: string; children: ReactNode }) {
  return (
    <div role="tabpanel" id={`panel-${id}`} aria-labelledby={`tab-${id}`}>
      {children}
    </div>
  );
}

// ---------- table ----------

export interface Column<R> {
  label: string;
  render: (row: R) => ReactNode;
  /** Hidden label on phones, for action cells. */
  bare?: boolean;
}

/** A checkbox that can also show "some" (indeterminate), for a select-all box. */
export function Checkbox({ checked, onChange, label }: { checked: boolean | 'some'; onChange: () => void; label: string }) {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = checked === 'some';
  }, [checked]);
  return <input ref={ref} type="checkbox" className={s.checkbox} aria-label={label} checked={checked === true} onChange={onChange} />;
}

/** Ticking rows: a box per row, and one in the header for every row shown. */
export interface TableSelection<R> {
  isSelected: (r: R) => boolean;
  onToggle: (r: R) => void;
  /** The header box: every row shown is ticked, some are, or none. */
  all: boolean | 'some';
  onToggleAll: () => void;
  /** The row's box, for screen readers ("Select line_items:6643"). */
  label: (r: R) => string;
}

export function Table<R>({
  columns,
  rows,
  rowKey,
  caption,
  selected,
  selection,
}: {
  columns: Column<R>[];
  rows: R[];
  rowKey: (r: R) => string;
  caption: string;
  selected?: (r: R) => boolean;
  selection?: TableSelection<R>;
}) {
  const isSelected = selection?.isSelected ?? selected;
  return (
    <div className={s.tableWrap}>
      <table className={s.table}>
        <caption className="visually-hidden">{caption}</caption>
        <thead>
          <tr>
            {selection && (
              <th scope="col" className={s.selectCell}>
                <Checkbox checked={selection.all} onChange={selection.onToggleAll} label="Select every record shown" />
              </th>
            )}
            {columns.map((c) => (
              <th key={c.label} scope="col">
                {c.bare ? <span className="visually-hidden">{c.label}</span> : c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={rowKey(r)} aria-selected={isSelected ? isSelected(r) : undefined}>
              {selection && (
                <td className={s.selectCell} data-label="">
                  <Checkbox checked={selection.isSelected(r)} onChange={() => selection.onToggle(r)} label={selection.label(r)} />
                </td>
              )}
              {columns.map((c) => (
                <td key={c.label} data-label={c.bare ? '' : c.label}>
                  {c.render(r)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Stays at the bottom of the screen while a list above it is worked through: what is selected, and what to do with it. */
export function ActionBar({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className={s.actionBar} role="region" aria-label={label}>
      {children}
    </div>
  );
}

// ---------- dialog ----------

/** A modal confirmation built on <dialog>: focus moves in, Escape closes, the page behind is inert. */
export function Dialog({ open, onClose, title, children, actions }: { open: boolean; onClose: () => void; title: ReactNode; children?: ReactNode; actions: ReactNode }) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) d.showModal();
    if (!open && d.open) d.close();
  }, [open]);
  return (
    <dialog ref={ref} className={s.dialog} aria-labelledby={titleId} onClose={onClose} onCancel={onClose}>
      {open && (
        <div className={s.dialogInner}>
          <h2 id={titleId}>{title}</h2>
          {children}
          <div className={s.dialogActions}>{actions}</div>
        </div>
      )}
    </dialog>
  );
}

// ---------- toasts ----------

interface Toast {
  id: number;
  text: string;
  tone: 'ok' | 'error';
}

const ToastContext = createContext<(text: string, tone?: 'ok' | 'error') => void>(() => undefined);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const push = useCallback((text: string, tone: 'ok' | 'error' = 'ok') => {
    const id = Date.now() + Math.random();
    setToasts((t) => [...t, { id, text, tone }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), tone === 'error' ? 8000 : 4000);
  }, []);
  return (
    <ToastContext.Provider value={push}>
      {children}
      <div className={s.toasts} aria-live="polite">
        {toasts.map((t) => (
          <div key={t.id} className={cx(s.toast, t.tone === 'error' && s.toastError)} role={t.tone === 'error' ? 'alert' : 'status'}>
            {t.text}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export const useToast = () => useContext(ToastContext);
